'use client';

import {
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownSection,
    DropdownTrigger,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { useState } from 'react';
import type { StudioAIFolder, StudioAIReport } from '../../types';

export function ReportItemMenu({
    report,
    folders,
    onOpen,
    onRename,
    onDelete,
    onShare,
    onMoveToFolder,
}: {
    report: StudioAIReport;
    folders: StudioAIFolder[];
    onOpen: () => void;
    onRename: () => void;
    onDelete: () => void;
    onShare: () => void;
    onMoveToFolder: (folderId: number | null) => void;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [view, setView] = useState<'main' | 'folders'>('main');

    const availableFolders = folders.filter((f) => f.id !== report.folderId);

    const close = () => {
        setIsOpen(false);
        setView('main');
    };

    const handleFolderSelect = (folderId: number | null) => {
        onMoveToFolder(folderId);
        close();
    };

    return (
        <Dropdown
            isOpen={isOpen}
            onOpenChange={(open) => {
                setIsOpen(open);
                if (!open) setView('main');
            }}
        >
            <DropdownTrigger className="flex h-7 w-7 min-w-7 items-center justify-center rounded-full hover:bg-default-100">
                <div>
                    <Icon icon="lucide:ellipsis" width={15} className="text-default-500" />
                </div>
            </DropdownTrigger>

            {view === 'folders' ? (
                <DropdownMenu
                    aria-label="Select folder"
                    closeOnSelect={false}
                    onAction={(key) => {
                        if (key === 'back') {
                            setView('main');
                            return;
                        }
                        if (key === 'remove-from-folder') {
                            handleFolderSelect(null);
                            return;
                        }
                        handleFolderSelect(Number(key));
                    }}
                >
                    <DropdownSection showDivider>
                        <DropdownItem
                            key="back"
                            startContent={
                                <Icon
                                    icon="lucide:arrow-left"
                                    width={14}
                                    className="text-default-400"
                                />
                            }
                            className="text-default-500"
                        >
                            Back
                        </DropdownItem>
                    </DropdownSection>
                    <DropdownSection
                        title="Move to folder"
                        items={[
                            ...availableFolders.map((f) => ({
                                key: String(f.id),
                                label: f.name,
                                isRemove: false,
                            })),
                            ...(report.folderId
                                ? [
                                      {
                                          key: 'remove-from-folder',
                                          label: 'Remove from folder',
                                          isRemove: true,
                                      },
                                  ]
                                : []),
                        ]}
                    >
                        {(item) => (
                            <DropdownItem
                                key={item.key}
                                startContent={
                                    <Icon
                                        icon={
                                            item.isRemove ? 'lucide:folder-minus' : 'lucide:folder'
                                        }
                                        width={14}
                                        className="text-warning"
                                    />
                                }
                                color={item.isRemove ? 'warning' : 'default'}
                            >
                                {item.label}
                            </DropdownItem>
                        )}
                    </DropdownSection>
                </DropdownMenu>
            ) : (
                <DropdownMenu
                    aria-label="Report actions"
                    closeOnSelect={false}
                    onAction={(key) => {
                        switch (key) {
                            case 'open':
                                onOpen();
                                close();
                                break;
                            case 'rename':
                                onRename();
                                close();
                                break;
                            case 'move-to-folder':
                                setView('folders');
                                break;
                            case 'share':
                                onShare();
                                close();
                                break;
                            case 'delete':
                                onDelete();
                                close();
                                break;
                        }
                    }}
                >
                    <DropdownSection showDivider>
                        <DropdownItem
                            key="open"
                            startContent={<Icon icon="lucide:external-link" width={14} />}
                        >
                            Open
                        </DropdownItem>
                        <DropdownItem
                            key="rename"
                            startContent={<Icon icon="lucide:edit-2" width={14} />}
                        >
                            Rename
                        </DropdownItem>
                        <DropdownItem
                            key="move-to-folder"
                            startContent={<Icon icon="lucide:folder-input" width={14} />}
                            endContent={
                                <Icon
                                    icon="lucide:chevron-right"
                                    width={14}
                                    className="text-default-400"
                                />
                            }
                        >
                            Move to folder
                        </DropdownItem>
                        <DropdownItem
                            key="share"
                            startContent={<Icon icon="lucide:share-2" width={14} />}
                        >
                            Share
                        </DropdownItem>
                    </DropdownSection>
                    <DropdownSection>
                        <DropdownItem
                            key="delete"
                            color="danger"
                            className="text-danger"
                            startContent={<Icon icon="lucide:trash-2" width={14} />}
                        >
                            Delete
                        </DropdownItem>
                    </DropdownSection>
                </DropdownMenu>
            )}
        </Dropdown>
    );
}
